import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { MarketplaceListing } from '../../../services/marketplaceService';
import { useMediaObjectUrl } from '../../../hooks/useMediaObjectUrl';
import { allowedLicenseTypes, originKey } from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import { VisibilityBadge } from '../Seller/SellerBadges';
import PriceBlock from './PriceBlock';
import '../marketplace-design.css';

export const listingPath = (listing: { slug?: string | null; id: number }): string =>
    `/marketplace/listings/${listing.slug || listing.id}`;

export const DIFFICULTY_KEYS: Record<string, string> = {
    beginner: 'marketplaceBuyer.difficulty.beginner',
    intermediate: 'marketplaceBuyer.difficulty.intermediate',
    advanced: 'marketplaceBuyer.difficulty.advanced',
};

/**
 * Kursbild nur, wenn ein echtes Thumbnail existiert. Kein Icon-Platzhalter mehr —
 * die Design-Vorlage (Marktplatz.html) hat keinen Bildbereich auf den Karten.
 */
export const ListingThumbnail: React.FC<{ src: string | null; height: number }> = ({ src, height }) => {
    const url = useMediaObjectUrl(src);
    if (!url) {
        return null;
    }
    return (
        <div className="mk-thumb" style={{ height }}>
            <img src={url} alt="" />
        </div>
    );
};

export interface ListingCardProps {
    listing: MarketplaceListing;
    canBuy: boolean;
    onLicense: (listing: MarketplaceListing) => void;
}

const MAX_CARD_COURSES = 4;

const totalLessons = (listing: MarketplaceListing): number =>
    (listing.courses ?? []).reduce((sum, c) => sum + (c.lessonCount ?? 0), 0);

const TYPE_LABEL_KEYS = {
    reference: 'marketplaceBuyer.price.reference',
    copy: 'marketplaceBuyer.price.copy',
    seat_copy: 'marketplaceBuyer.price.seat',
} as const;

/**
 * Angebotskarte im Marktplatz (Design „Marktplatz“, `.ocard`). Das Herkunfts-
 * Badge (Öffentlich: grauer Outline-Pill mit `storefront`; Gruppe: gefüllter
 * Info-Pill mit `groups`) steht immer oben — beide Bereiche müssen auch auf
 * der Karte unverwechselbar sein. „Lizenziert“ steht als grüner Status-Pill
 * rechts daneben.
 */
const ListingCard: React.FC<ListingCardProps> = ({ listing, canBuy, onLicense }) => {
    const translate = useTranslate();
    const types = allowedLicenseTypes(listing);
    const description = listing.description ?? '';

    return (
        <article className="mk-ocard" data-testid={`listing-card-${listing.id}`} data-origin={originKey(listing)}>
            <div className="mk-obody">
                <div className="mk-otop">
                    <VisibilityBadge visibility={originKey(listing)} short />
                    <span className="sp" />
                    {listing.alreadyLicensed && (
                        <span className="mk-pill ok">
                            <MaterialSymbol name="check" />
                            {translate('marketplaceBuyer.card.licensed')}
                        </span>
                    )}
                </div>
                <h3>{listing.title}</h3>
                {listing.isBundle && (
                    <div className="mk-ometa" style={{ marginBottom: 8 }}>
                        <span className="mk-pill neu" data-testid={`bundle-pill-${listing.id}`}>
                            <MaterialSymbol name="layers" />
                            {translate('marketplaceBuyer.bundle.pill', { count: listing.courseCount ?? listing.courses?.length ?? 0 })}
                        </span>
                    </div>
                )}
                {description && <p className="desc">{description.length > 100 ? `${description.substring(0, 100)}…` : description}</p>}
                {listing.isBundle && !!listing.courses?.length && (
                    <ol className="mk-ocourses" data-testid={`bundle-courses-${listing.id}`}>
                        {listing.courses.slice(0, MAX_CARD_COURSES).map((c, i) => (
                            <li key={c.id}>
                                <span className="n">{i + 1}.</span>
                                <span className="t">{c.title}</span>
                            </li>
                        ))}
                        {listing.courses.length > MAX_CARD_COURSES && (
                            <li className="more">{translate('marketplaceBuyer.bundle.more', { count: listing.courses.length - MAX_CARD_COURSES })}</li>
                        )}
                    </ol>
                )}
                <div className="mk-ometa">
                    {!!listing.course.lessonCount && !listing.isBundle && (
                        <span className="mk-pill neu">{translate('marketplaceBuyer.card.lessons', { count: listing.course.lessonCount })}</span>
                    )}
                    {listing.isBundle && totalLessons(listing) > 0 && (
                        <span className="mk-pill neu">{translate('marketplaceBuyer.card.lessons', { count: totalLessons(listing) })}</span>
                    )}
                    {listing.course.difficulty && !listing.isBundle && (
                        <span className="mk-pill neu">
                            {translate(DIFFICULTY_KEYS[listing.course.difficulty] ?? listing.course.difficulty, { _: listing.course.difficulty })}
                        </span>
                    )}
                    {listing.category && <span className="mk-pill neu">{listing.category.name}</span>}
                </div>
                <div className="mk-prices">
                    {types.map((type) => (
                        <div className="mk-price" key={type}>
                            <div className="k">{translate(TYPE_LABEL_KEYS[type])}</div>
                            <div className="p">
                                <PriceBlock listing={listing} type={type} />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mk-ofoot">
                    <RouterLink to={listingPath(listing)} className="mk-lnk">
                        {translate('marketplaceBuyer.card.details')}
                        <MaterialSymbol name="arrow_forward" />
                    </RouterLink>
                    {listing.alreadyLicensed ? (
                        <button type="button" className="mk-btn-s mk-sm mk-done" disabled>
                            <MaterialSymbol name="check" />
                            {translate('marketplaceBuyer.card.licensed')}
                        </button>
                    ) : (
                        canBuy && (
                            <button type="button" className="mk-btn-s mk-sm" onClick={() => onLicense(listing)}>
                                <MaterialSymbol name="shopping_cart" />
                                {translate('marketplaceBuyer.card.license')}
                            </button>
                        )
                    )}
                </div>
            </div>
        </article>
    );
};

export default ListingCard;
